import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { repertoryData } from '../data/repertory';
import { events2024, events2025 } from '../data/calendar';

// Enhanced Hero Section with overlay and CTA
function HeroSection() {
  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Background Image with zoom animation */}
      <motion.div
        className="absolute inset-0 bg-main-photo bg-cover bg-center"
        initial={{ scale: 1 }}
        animate={{ scale: 1.05 }}
        transition={{ duration: 20, ease: 'linear', repeat: Infinity, repeatType: 'reverse' }}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />

      {/* Content */}
      <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
        <motion.h1
          className="text-6xl md:text-7xl lg:text-8xl font-righteous text-white mb-6 drop-shadow-2xl"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          Sublime Dance Company
        </motion.h1>

        <motion.p
          className="text-xl md:text-2xl lg:text-3xl text-white/90 mb-12 max-w-3xl leading-relaxed"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
        >
          Exploring the essential desire that constantly draws us to movement
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
        >
          <Link
            to="/repertory"
            className="px-8 py-4 bg-golden text-white font-semibold text-lg rounded-lg hover:bg-golden/90 transform hover:scale-105 transition-all duration-300 shadow-xl"
          >
            Explore Our Work
          </Link>
          <Link
            to="/calendar"
            className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold text-lg rounded-lg hover:bg-white/20 border-2 border-white/30 transform hover:scale-105 transition-all duration-300"
          >
            Upcoming Shows
          </Link>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ duration: 2, delay: 1.5, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/70 rounded-full" />
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// Mission Statement Section
function MissionSection() {
  const words = ['explore', 'question', 'build', 'create', 'move'];

  return (
    <section className="py-20 md:py-32 px-6 bg-zinc-100">
      <motion.div
        className="max-w-5xl mx-auto text-center"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-righteous text-gray-800 mb-8 leading-tight">
          We aim to <span className="text-golden">explore</span>, <span className="text-golden">question</span>,
          <br className="hidden md:block" /> and <span className="text-golden">build upon</span> movement
        </h2>
        <p className="text-xl md:text-2xl text-gray-600 leading-relaxed">
          Through contemporary dance, we push boundaries and create meaningful artistic experiences
        </p>
      </motion.div>
    </section>
  );
}

// Featured Performance Section
function FeaturedPerformance() {
  const featured = repertoryData.find(p => p.title === 'Quem Roubou o Natal?') || repertoryData[0];

  return (
    <section className="py-20 md:py-32 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.h2
          className="text-4xl md:text-5xl font-righteous text-center text-gray-800 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          Featured Work
        </motion.h2>

        <motion.div
          className="grid md:grid-cols-2 gap-12 items-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
        >
          <div className="relative group overflow-hidden rounded-2xl shadow-2xl">
            <img
              src={featured.img}
              alt={featured.title}
              className="w-full h-[400px] md:h-[600px] object-cover transform group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>

          <div className="space-y-6">
            <span className="inline-block px-4 py-2 bg-golden/10 text-golden font-semibold rounded-full">
              {featured.date}
            </span>
            <h3 className="text-4xl md:text-5xl font-righteous text-gray-800">{featured.title}</h3>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Our latest creation, a captivating performance that explores themes of tradition, joy, and transformation
              through contemporary movement.
            </p>
            <Link
              to={`/repertory/${featured.link_to}`}
              className="inline-block px-8 py-4 bg-golden text-white font-semibold text-lg rounded-lg hover:bg-golden/90 transform hover:scale-105 transition-all duration-300 shadow-lg"
            >
              Learn More
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Upcoming Events Preview
function UpcomingEvents() {
  const upcomingEvents = events2025.slice(0, 1);

  return (
    <section className="py-20 md:py-32 px-6 bg-zinc-100">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-righteous text-gray-800 mb-4">Upcoming Events</h2>
          <p className="text-xl text-gray-600">Join us for these upcoming performances and events</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {upcomingEvents.map((event, index) => (
            <motion.div
              key={index}
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onClick={() => window.open(event.links[0].eventLink, '_blank')}
            >
              <div className="text-golden font-semibold mb-3">{event.when}</div>
              <h3 className="text-xl font-righteous text-gray-800 mb-3">{event.what}</h3>
              <p className="text-gray-600 mb-4">{event.where}</p>
              {event.links[0].videoLink && (
                <a
                  href={event.links[0].videoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-golden hover:text-gray-700 font-semibold transition-colors"
                >
                  Watch Preview →
                </a>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Link
            to="/calendar"
            className="inline-block px-8 py-4 bg-white text-golden border-2 border-golden font-semibold text-lg rounded-lg hover:bg-golden hover:text-white transform hover:scale-105 transition-all duration-300"
          >
            View Full Calendar
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

// Latest Work Grid
function LatestWorkGrid() {
  const latestWork = repertoryData.slice(0, 6);

  return (
    <section className="py-20 md:py-32 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.h2
          className="text-4xl md:text-5xl font-righteous text-center text-gray-800 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          Our Repertory
        </motion.h2>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {latestWork.map((work, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link to={`/repertory/${work.link_to}`} className="group block">
                <div className="relative overflow-hidden rounded-lg shadow-lg aspect-square">
                  <img
                    src={work.img}
                    alt={work.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-white font-righteous text-lg mb-1">{work.title}</h3>
                    <span className="text-golden text-sm">{work.date}</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Link
            to="/repertory"
            className="inline-block px-8 py-4 bg-golden text-white font-semibold text-lg rounded-lg hover:bg-golden/90 transform hover:scale-105 transition-all duration-300 shadow-lg"
          >
            View All Works
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

// Final CTA Section
function CTASection() {
  return (
    <section className="py-20 md:py-32 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <motion.div
        className="max-w-4xl mx-auto text-center"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-righteous mb-6">Let's Create Together</h2>
        <p className="text-xl md:text-2xl text-gray-300 mb-12">
          Interested in collaborations, booking, or learning more about our work?
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/contact-us"
            className="px-8 py-4 bg-golden text-white font-semibold text-lg rounded-lg hover:bg-golden/90 transform hover:scale-105 transition-all duration-300 shadow-xl"
          >
            Get in Touch
          </Link>
          <Link
            to="/company"
            className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold text-lg rounded-lg hover:bg-white/20 border-2 border-white/30 transform hover:scale-105 transition-all duration-300"
          >
            About Us
          </Link>
        </div>
      </motion.div>
    </section>
  );
}

// Main Home Component
function Home() {
  return (
    <div className="">
      <HeroSection />
      <MissionSection />
      <FeaturedPerformance />
      <UpcomingEvents />
      <LatestWorkGrid />
      <CTASection />
    </div>
  );
}

export default Home;
